import React, { createContext, useContext } from 'react';

/**
 * ImageSourceContext
 * 
 * Handles local image path routing for card images.
 * All images are served from the local Linux server.
 * 
 * Usage:
 *   const { getImageUrl } = useImageSource();
 *   const imageUrl = getImageUrl(card);
 */

const ImageSourceContext = createContext();

export const ImageSourceProvider = ({ children }) => {
    /**
     * Get the image URL for a card using local server paths only
     * @param {Object} card - Card object with image_path, local_image_path, backside_image_path, local_backside_path
     * @param {boolean} isBackside - Whether to get the backside image
     * @returns {string|null} The image URL to use, or null if no local path exists
     */
    const getImageUrl = (card, isBackside = false) => {
        if (!card) return null;

        const baseUrl = process.env.REACT_APP_IMAGE_BASE_URL || 'https://thetraderspot.com';
        
        const buildImageUrl = (path) => {
            if (!path) return null;
            // If already a full URL, normalize old localhost URLs to production
            if (path.startsWith('http://') || path.startsWith('https://')) {
                // Normalize old localhost URLs to production
                if (path.includes('localhost:5001') || path.includes('localhost:5000')) {
                    const cleanPath = path.replace(/https?:\/\/localhost:\d+\//g, '/').replace(/\/+/g, '/');
                    return `${baseUrl}${cleanPath}`;
                }
                // If it's already a production URL, return as-is
                if (path.includes('thetraderspot.com')) {
                    return path;
                }
                // Reject any other external URLs (including Scryfall)
                return null;
            }
            // Prepend base URL for relative paths
            const cleanPath = path.replace(/\/+/g, '/');
            return `${baseUrl}${cleanPath.startsWith('/') ? '' : '/'}${cleanPath}`;
        };

        if (isBackside) {
            // Priority 1: local_backside_path (from collection_cards)
            if (card.local_backside_path) {
                return buildImageUrl(card.local_backside_path);
            }
            // Priority 2: backside_image_path (from mtg_cards) - only if local path
            if (card.backside_image_path) {
                return buildImageUrl(card.backside_image_path);
            }
            return null;
        } else {
            // Priority 1: local_image_path (from collection_cards)
            if (card.local_image_path) {
                return buildImageUrl(card.local_image_path);
            }
            // Priority 2: image_path (from mtg_cards) - only if local path
            if (card.image_path) {
                return buildImageUrl(card.image_path);
            }
            return null;
        }
    };

    return (
        <ImageSourceContext.Provider value={{ getImageUrl }}>
            {children}
        </ImageSourceContext.Provider>
    );
};

export const useImageSource = () => {
    const context = useContext(ImageSourceContext);
    if (!context) {
        throw new Error('useImageSource must be used within an ImageSourceProvider');
    }
    return context;
};

export default ImageSourceContext;
